// src/pages/Citation.jsx - Redesigned with Modern Theme
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import SearchNavigation from '../components/SearchNavigation';
import SearchableDropdown from '../components/SearchableDropdown';
import ApiService from '../services/apiService';

const Citation = () => {
  const navigate = useNavigate();

  // State for selected values
  const [selectedJournal, setSelectedJournal] = useState('Select a option');
  const [selectedYear, setSelectedYear] = useState('Select a option');
  const [selectedVolume, setSelectedVolume] = useState('Select a option');
  const [selectedPage, setSelectedPage] = useState('Select a option');

  // State for dropdown visibility
  const [isJournalOpen, setIsJournalOpen] = useState(false);
  const [isYearOpen, setIsYearOpen] = useState(false);
  const [isVolumeOpen, setIsVolumeOpen] = useState(false);
  const [isPageOpen, setIsPageOpen] = useState(false);

  // State for API data
  const [journals, setJournals] = useState([]);
  const [years, setYears] = useState([]);
  const [volumes, setVolumes] = useState([]);
  const [pages, setPages] = useState([]);

  // Loading states
  const [isLoadingJournals, setIsLoadingJournals] = useState(true);
  const [isLoadingYears, setIsLoadingYears] = useState(false);
  const [isLoadingVolumes, setIsLoadingVolumes] = useState(false);
  const [isLoadingPages, setIsLoadingPages] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Error states
  const [journalsError, setJournalsError] = useState('');
  const [yearsError, setYearsError] = useState('');
  const [volumesError, setVolumesError] = useState('');
  const [pagesError, setPagesError] = useState('');

  // API base URL
  const API_BASE_URL = 'https://api.ljsoftonline.com';

  useEffect(() => {
    loadJournals();
  }, []);

  // Authenticated API request helper
  const makeAuthenticatedRequest = async (url) => {
    const token = ApiService.getAccessToken();

    if (!token) {
      throw new Error('No access token found. Please login again.');
    }

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      }
    });

    if (response.status === 401) {
      ApiService.clearTokensAndRedirect();
      throw new Error('Session expired. Please login again.');
    }

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return response.json();
  };

  // API Methods
  const loadJournals = async () => {
    try {
      setIsLoadingJournals(true);
      setJournalsError('');
      console.log('🔍 Loading journals...');

      const data = await makeAuthenticatedRequest(`${API_BASE_URL}/Journals`);
      setJournals(data);
      console.log('✅ Journals loaded:', data);
    } catch (error) {
      console.error('❌ Error loading journals:', error);
      setJournalsError(error.message);
    } finally {
      setIsLoadingJournals(false);
    }
  };

  const loadYears = async (journalName) => {
    try {
      setIsLoadingYears(true);
      setYearsError('');
      console.log('🔍 Loading years for:', journalName);

      const data = await makeAuthenticatedRequest(`${API_BASE_URL}/Journals/${encodeURIComponent(journalName)}/years`);
      setYears(data);
      console.log('✅ Years loaded for', journalName, ':', data);
    } catch (error) {
      console.error('❌ Error loading years:', error);
      setYearsError(error.message);
      setYears([]);
    } finally {
      setIsLoadingYears(false);
    }
  };

  const loadVolumes = async (journalName, year) => {
    try {
      setIsLoadingVolumes(true);
      setVolumesError('');
      console.log('🔍 Loading volumes for:', journalName, year);

      const data = await makeAuthenticatedRequest(`${API_BASE_URL}/Journals/${encodeURIComponent(journalName)}/years/${year}/volumes`);
      setVolumes(data);
      console.log('✅ Volumes loaded for', journalName, year, ':', data);
    } catch (error) {
      console.error('❌ Error loading volumes:', error);
      setVolumesError(error.message);
      setVolumes([]);
    } finally {
      setIsLoadingVolumes(false);
    }
  };

  const loadPages = async (journalName, year, volume) => {
    try {
      setIsLoadingPages(true);
      setPagesError('');
      console.log('🔍 Loading pages for:', journalName, year, volume);

      const data = await makeAuthenticatedRequest(`${API_BASE_URL}/Journals/${encodeURIComponent(journalName)}/years/${year}/volumes/${encodeURIComponent(volume)}/pages`);
      setPages(data);
      console.log('✅ Pages loaded for', journalName, year, volume, ':', data);
    } catch (error) {
      console.error('❌ Error loading pages:', error);
      setPagesError(error.message);
      setPages([]);
    } finally {
      setIsLoadingPages(false);
    }
  };

  // Event Handlers
  const handleJournalSelect = (journal) => {
    setSelectedJournal(journal);
    setIsJournalOpen(false);

    // Reset dependent dropdowns
    setSelectedYear('Select a option');
    setSelectedVolume('Select a option');
    setSelectedPage('Select a option');
    setYears([]);
    setVolumes([]);
    setPages([]);

    // Clear errors
    setYearsError('');
    setVolumesError('');
    setPagesError('');

    // Load years for selected journal
    if (journal !== 'Select a option') {
      loadYears(journal);
    }
  };

  const handleYearSelect = (year) => {
    setSelectedYear(year);
    setIsYearOpen(false);

    // Reset dependent dropdowns
    setSelectedVolume('Select a option');
    setSelectedPage('Select a option');
    setVolumes([]);
    setPages([]);

    // Clear errors
    setVolumesError('');
    setPagesError('');

    // Load volumes for selected journal and year
    if (year !== 'Select a option' && selectedJournal !== 'Select a option') {
      loadVolumes(selectedJournal, year);
    }
  };

  const handleVolumeSelect = (volume) => {
    setSelectedVolume(volume);
    setIsVolumeOpen(false);

    // Reset dependent dropdown
    setSelectedPage('Select a option');
    setPages([]);

    // Clear errors
    setPagesError('');

    // Load pages for selected journal, year, and volume
    if (volume !== 'Select a option' && selectedJournal !== 'Select a option' && selectedYear !== 'Select a option') {
      loadPages(selectedJournal, selectedYear, volume);
    }
  };

  const handlePageSelect = (page) => {
    setSelectedPage(page);
    setIsPageOpen(false);
  };

  const handleSearch = async (type) => {
    console.log('Search type:', type);

    // Validate required fields
    if (selectedJournal === 'Select a option') {
      alert('Please select a journal');
      return;
    }

    // Collect search data
    const searchData = {
      journal: selectedJournal,
      year: selectedYear !== 'Select a option' ? selectedYear : null,
      volume: selectedVolume !== 'Select a option' && selectedVolume !== "0" ? selectedVolume : null,
      page: selectedPage !== 'Select a option' ? selectedPage : null,
      searchType: type
    };

    console.log('🔍 Citation search data:', searchData);

    try {
      setIsLoading(true);

      const apiResponse = await ApiService.searchCitation(searchData);

      console.log('✅ Citation Search Results:', apiResponse);

      const searchResults = apiResponse.hits || [];
      const totalCount = apiResponse.total || 0;
      const courtsList = apiResponse.courtsList || [];
      const yearList = apiResponse.yearList || [];

      const resultsData = {
        results: searchResults,
        totalCount: totalCount,
        query: `${searchData.journal} ${searchData.year || ''} ${searchData.volume || ''} ${searchData.page || ''}`.trim(),
        searchType: 'Citation Search',
        timestamp: new Date().toISOString(),
        courtsList: courtsList,
        yearList: yearList,
        searchData: searchData
      };

      console.log('💾 Storing citation results with API filter data:', resultsData);
      localStorage.setItem('searchResults', JSON.stringify(resultsData));

      navigate('/results');

    } catch (error) {
      console.error('Citation search failed:', error);
      alert(`Citation search failed: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const getDropdownIcon = (field) => {
    switch(field) {
      case 'journal': return 'bx-book-open';
      case 'year': return 'bx-calendar';
      case 'volume': return 'bx-book';
      case 'page': return 'bx-file';
      default: return 'bx-chevron-down';
    }
  };

  return (
    <div className="citation-layout">
      <Navbar />
      <SearchNavigation />

      <div className="citation-main-content">
        {/* Hero Section */}
        <div className="citation-hero">
          <div className="hero-badge">
            <i className="bx bx-book-bookmark"></i>
            <span>Citation Search</span>
          </div>
          <h1 className="hero-title">
            Law Journals of India
          </h1>
          <p className="hero-subtitle">
            Search through comprehensive legal citations and journals
          </p>
        </div>

        <div className="citation-search-container">
          {/* Search Card */}
          <div className="citation-card">
            <div className="card-header">
              <div className="header-icon">
                <i className="bx bx-search-alt"></i>
              </div>
              <div className="header-content">
                <h2 className="card-title">Citation Details</h2>
                <p className="card-subtitle">Select journal, year, volume, and page number</p>
              </div>
            </div>

            <div className="citation-form">
              {/* Row 1: Journal and Year */}
              <div className="form-row">
                {/* Journal Dropdown */}
                <div className="form-field">
                  <label className="field-label">
                    <i className={`bx ${getDropdownIcon('journal')}`}></i>
                    Journal Name
                    <span className="required">*</span>
                  </label>
                  <div className="dropdown-container">
                    <button
                      className={`modern-dropdown-btn ${selectedJournal !== 'Select a option' ? 'selected' : ''}`}
                      onClick={() => setIsJournalOpen(!isJournalOpen)}
                      disabled={isLoadingJournals}
                    >
                      <span className="dropdown-text">
                        {isLoadingJournals ? (
                          <>
                            <i className="bx bx-loader-alt bx-spin"></i>
                            Loading journals...
                          </>
                        ) : selectedJournal}
                      </span>
                      <i className={`bx bx-chevron-down dropdown-arrow ${isJournalOpen ? 'open' : ''}`}></i>
                    </button>

                    {(isJournalOpen || isLoadingJournals || journalsError) && (
                      <>
                        <div
                          className="dropdown-backdrop"
                          onClick={() => setIsJournalOpen(false)}
                        />
                        <SearchableDropdown
                          items={journals}
                          selectedItem={selectedJournal}
                          onSelect={handleJournalSelect}
                          isOpen={isJournalOpen}
                          onToggle={setIsJournalOpen}
                          isLoading={isLoadingJournals}
                          error={journalsError}
                          loadingText="Loading journals..."
                          placeholder="journals"
                        />
                      </>
                    )}
                  </div>
                  {journalsError && (
                    <div className="field-error">
                      <i className="bx bx-error-circle"></i>
                      {journalsError}
                    </div>
                  )}
                </div>

                {/* Year Dropdown */}
                <div className="form-field">
                  <label className="field-label">
                    <i className={`bx ${getDropdownIcon('year')}`}></i>
                    Year
                  </label>
                  <div className="dropdown-container">
                    <button
                      className={`modern-dropdown-btn ${selectedYear !== 'Select a option' ? 'selected' : ''}`}
                      onClick={() => setIsYearOpen(!isYearOpen)}
                      disabled={isLoadingYears || selectedJournal === 'Select a option'}
                    >
                      <span className="dropdown-text">
                        {isLoadingYears ? (
                          <>
                            <i className="bx bx-loader-alt bx-spin"></i>
                            Loading years...
                          </>
                        ) : selectedYear}
                      </span>
                      <i className={`bx bx-chevron-down dropdown-arrow ${isYearOpen ? 'open' : ''}`}></i>
                    </button>

                    {(isYearOpen || isLoadingYears || yearsError) && selectedJournal !== 'Select a option' && (
                      <>
                        <div
                          className="dropdown-backdrop"
                          onClick={() => setIsYearOpen(false)}
                        />
                        <SearchableDropdown
                          items={years}
                          selectedItem={selectedYear}
                          onSelect={handleYearSelect}
                          isOpen={isYearOpen}
                          onToggle={setIsYearOpen}
                          isLoading={isLoadingYears}
                          error={yearsError}
                          loadingText="Loading years..."
                          placeholder="years"
                        />
                      </>
                    )}
                  </div>
                  {yearsError && (
                    <div className="field-error">
                      <i className="bx bx-error-circle"></i>
                      {yearsError}
                    </div>
                  )}
                </div>
              </div>

              {/* Row 2: Volume and Page */}
              <div className="form-row">
                {/* Volume Dropdown */}
                <div className="form-field">
                  <label className="field-label">
                    <i className={`bx ${getDropdownIcon('volume')}`}></i>
                    Volume
                  </label>
                  <div className="dropdown-container">
                    <button
                      className={`modern-dropdown-btn ${selectedVolume !== 'Select a option' ? 'selected' : ''}`}
                      onClick={() => setIsVolumeOpen(!isVolumeOpen)}
                      disabled={isLoadingVolumes || selectedYear === 'Select a option'}
                    >
                      <span className="dropdown-text">
                        {isLoadingVolumes ? (
                          <>
                            <i className="bx bx-loader-alt bx-spin"></i>
                            Loading volumes...
                          </>
                        ) : selectedVolume}
                      </span>
                      <i className={`bx bx-chevron-down dropdown-arrow ${isVolumeOpen ? 'open' : ''}`}></i>
                    </button>

                    {(isVolumeOpen || isLoadingVolumes || volumesError) && selectedYear !== 'Select a option' && (
                      <>
                        <div
                          className="dropdown-backdrop"
                          onClick={() => setIsVolumeOpen(false)}
                        />
                        <SearchableDropdown
                          items={volumes}
                          selectedItem={selectedVolume}
                          onSelect={handleVolumeSelect}
                          isOpen={isVolumeOpen}
                          onToggle={setIsVolumeOpen}
                          isLoading={isLoadingVolumes}
                          error={volumesError}
                          loadingText="Loading volumes..."
                          placeholder="volumes"
                        />
                      </>
                    )}
                  </div>
                  {volumesError && (
                    <div className="field-error">
                      <i className="bx bx-error-circle"></i>
                      {volumesError}
                    </div>
                  )}
                </div>

                {/* Page Dropdown */}
                <div className="form-field">
                  <label className="field-label">
                    <i className={`bx ${getDropdownIcon('page')}`}></i>
                    Page
                  </label>
                  <div className="dropdown-container">
                    <button
                      className={`modern-dropdown-btn ${selectedPage !== 'Select a option' ? 'selected' : ''}`}
                      onClick={() => setIsPageOpen(!isPageOpen)}
                      disabled={isLoadingPages || selectedVolume === 'Select a option'}
                    >
                      <span className="dropdown-text">
                        {isLoadingPages ? (
                          <>
                            <i className="bx bx-loader-alt bx-spin"></i>
                            Loading pages...
                          </>
                        ) : selectedPage}
                      </span>
                      <i className={`bx bx-chevron-down dropdown-arrow ${isPageOpen ? 'open' : ''}`}></i>
                    </button>

                    {(isPageOpen || isLoadingPages || pagesError) && selectedVolume !== 'Select a option' && (
                      <>
                        <div
                          className="dropdown-backdrop"
                          onClick={() => setIsPageOpen(false)}
                        />
                        <SearchableDropdown
                          items={pages}
                          selectedItem={selectedPage}
                          onSelect={handlePageSelect}
                          isOpen={isPageOpen}
                          onToggle={setIsPageOpen}
                          isLoading={isLoadingPages}
                          error={pagesError}
                          loadingText="Loading pages..."
                          placeholder="pages"
                        />
                      </>
                    )}
                  </div>
                  {pagesError && (
                    <div className="field-error">
                      <i className="bx bx-error-circle"></i>
                      {pagesError}
                    </div>
                  )}
                </div>
              </div>

              {/* Search Button */}
              <div className="form-actions">
                <button
                  className="search-button"
                  onClick={() => handleSearch('citations')}
                  disabled={selectedJournal === 'Select a option' || isLoading}
                >
                  {isLoading ? (
                    <>
                      <i className="bx bx-loader-alt bx-spin"></i>
                      Searching...
                    </>
                  ) : (
                    <>
                      <i className="bx bx-search"></i>
                      Search Citations
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>

          {/* Info Cards */}
          <div className="info-cards">
            <div className="info-card">
              <div className="info-icon">
                <i className="bx bx-info-circle"></i>
              </div>
              <div className="info-content">
                <h3>How it works</h3>
                <p>Select a journal name to view available years, then choose volume and page for precise results.</p>
              </div>
            </div>

            <div className="info-card">
              <div className="info-icon">
                <i className="bx bx-star"></i>
              </div>
              <div className="info-content">
                <h3>Comprehensive Database</h3>
                <p>Access to India's most extensive collection of law journals and legal citations.</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        /* ============================================
           BASE LAYOUT
        ============================================ */
        .citation-layout {
          min-height: 100vh;
          background: #fafafa;
        }

        .citation-main-content {
          padding-top: 140px;
          width: 100%;
        }

        /* ============================================
           HERO SECTION
        ============================================ */
        .citation-hero {
          background: linear-gradient(135deg, #E91E63 0%, #E91E64 25%, #c41849 75%, #D81B60 100%);
          padding: 60px 20px;
          text-align: center;
          color: white;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
        }

        .hero-badge {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 10px 24px;
          background: rgba(255, 255, 255, 0.2);
          backdrop-filter: blur(10px);
          border: 1px solid rgba(255, 255, 255, 0.3);
          border-radius: 50px;
          font-size: 14px;
          font-weight: 600;
          margin-bottom: 20px;
        }

        .hero-badge i {
          font-size: 18px;
        }

        .hero-title {
          font-size: 2.5rem;
          font-weight: 800;
          margin: 0 0 12px 0;
          text-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
          letter-spacing: -0.5px;
          line-height: 1.2;
        }

        .hero-subtitle {
          font-size: 1.1rem;
          opacity: 0.95;
          margin: 0;
          font-weight: 400;
        }

        /* ============================================
           SEARCH CONTAINER
        ============================================ */
        .citation-search-container {
          max-width: 1000px;
          margin: -40px auto 0;
          padding: 0 20px 60px;
          position: relative;
          z-index: 10;
        }

        /* ============================================
           CITATION CARD
        ============================================ */
        .citation-card {
          background: white;
          border-radius: 24px;
          padding: 0;
          box-shadow: 0 10px 40px rgba(0, 0, 0, 0.08);
          overflow: hidden;
          margin-bottom: 30px;
        }

        .card-header {
          background: linear-gradient(135deg, rgba(245, 166, 35, 0.1), rgba(233, 30, 99, 0.05));
          padding: 30px 35px;
          display: flex;
          align-items: center;
          gap: 20px;
          border-bottom: 2px solid #f5f5f5;
        }

        .header-icon {
          width: 60px;
          height: 60px;
          background: linear-gradient(135deg, #E91E63, #c41849);
          border-radius: 16px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-size: 28px;
          flex-shrink: 0;
          box-shadow: 0 4px 15px rgba(245, 166, 35, 0.3);
        }

        .header-content {
          flex: 1;
        }

        .card-title {
          font-size: 24px;
          font-weight: 700;
          color: #333;
          margin: 0 0 5px 0;
        }

        .card-subtitle {
          font-size: 14px;
          color: #666;
          margin: 0;
        }

        /* ============================================
           FORM
        ============================================ */
        .citation-form {
          padding: 35px;
        }

        .form-row {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 25px;
          margin-bottom: 25px;
        }

        .form-field {
          display: flex;
          flex-direction: column;
          gap: 10px;
        }

        .field-label {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 14px;
          font-weight: 700;
          color: #555;
        }

        .field-label i {
          font-size: 18px;
          color: #E91E63;
        }

        .required {
          color: #c41849;
          margin-left: 2px;
        }

        /* ============================================
           MODERN DROPDOWN
        ============================================ */
        .dropdown-container {
          position: relative;
        }

        .modern-dropdown-btn {
          width: 100%;
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 14px 18px;
          background: #fafafa;
          border: 2px solid #e0e0e0;
          border-radius: 12px;
          font-size: 15px;
          color: #666;
          cursor: pointer;
          transition: all 0.3s ease;
          font-weight: 500;
        }

        .modern-dropdown-btn:hover:not(:disabled) {
          border-color: #E91E63;
          background: white;
        }

        .modern-dropdown-btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
          background: #f5f5f5;
        }

        .modern-dropdown-btn.selected {
          border-color: #c41849;
          background: white;
          color: #333;
          font-weight: 600;
        }

        .dropdown-text {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .dropdown-arrow {
          font-size: 20px;
          color: #999;
          transition: transform 0.3s ease;
        }

        .dropdown-arrow.open {
          transform: rotate(180deg);
        }

        .dropdown-backdrop {
          position: fixed;
          inset: 0;
          z-index: 999;
        }

        /* ============================================
           FIELD ERROR
        ============================================ */
        .field-error {
          display: flex;
          align-items: center;
          gap: 6px;
          font-size: 13px;
          color: #c41849;
          padding: 8px 12px;
          background: linear-gradient(135deg, rgba(233, 30, 99, 0.1), rgba(220, 38, 38, 0.05));
          border-radius: 8px;
          border-left: 3px solid #c41849;
        }

        .field-error i {
          font-size: 16px;
        }

        /* ============================================
           SEARCH BUTTON
        ============================================ */
        .form-actions {
          display: flex;
          justify-content: center;
          margin-top: 30px;
        }

        .search-button {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 16px 40px;
          background: linear-gradient(135deg, #E91E63, #c41849);
          color: white;
          border: none;
          border-radius: 12px;
          font-size: 16px;
          font-weight: 700;
          cursor: pointer;
          transition: all 0.3s ease;
          box-shadow: 0 4px 20px rgba(245, 166, 35, 0.3);
        }

        .search-button i {
          font-size: 20px;
        }

        .search-button:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 6px 25px rgba(233, 30, 99, 0.4);
        }

        .search-button:disabled {
          opacity: 0.6;
          cursor: not-allowed;
          transform: none;
        }

        /* ============================================
           INFO CARDS
        ============================================ */
        .info-cards {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 20px;
        }

        .info-card {
          background: white;
          border-radius: 16px;
          padding: 24px;
          display: flex;
          gap: 16px;
          box-shadow: 0 4px 15px rgba(0, 0, 0, 0.05);
          transition: all 0.3s ease;
        }

        .info-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 8px 25px rgba(245, 166, 35, 0.15);
        }

        .info-icon {
          width: 48px;
          height: 48px;
          background: linear-gradient(135deg, rgba(245, 166, 35, 0.15), rgba(233, 30, 99, 0.1));
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #E91E63;
          font-size: 24px;
          flex-shrink: 0;
        }

        .info-content h3 {
          font-size: 16px;
          font-weight: 700;
          color: #333;
          margin: 0 0 8px 0;
        }

        .info-content p {
          font-size: 14px;
          color: #666;
          margin: 0;
          line-height: 1.5;
        }

        /* ============================================
           ANIMATIONS
        ============================================ */
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        .bx-spin {
          animation: spin 1s linear infinite;
        }

        /* ============================================
           RESPONSIVE DESIGN
        ============================================ */
        @media (max-width: 768px) {
          .citation-main-content {
            padding-top: 130px;
          }

          .citation-hero {
            padding: 40px 15px;
          }

          .hero-title {
            font-size: 1.8rem;
          }

          .hero-subtitle {
            font-size: 0.95rem;
          }

          .citation-search-container {
            margin-top: -30px;
            padding: 0 15px 40px;
          }

          .card-header {
            padding: 20px;
            flex-direction: column;
            text-align: center;
          }

          .header-icon {
            width: 50px;
            height: 50px;
            font-size: 24px;
          }

          .card-title {
            font-size: 20px;
          }

          .citation-form {
            padding: 20px;
          }

          .form-row {
            grid-template-columns: 1fr;
            gap: 20px;
          }

          .info-cards {
            grid-template-columns: 1fr;
          }

          .search-button {
            width: 100%;
            justify-content: center;
          }
        }

        @media (max-width: 480px) {
          .hero-title {
            font-size: 1.5rem;
          }

          .card-header {
            padding: 16px;
          }

          .citation-form {
            padding: 16px;
          }

          .modern-dropdown-btn {
            padding: 12px 14px;
            font-size: 14px;
          }

          .search-button {
            padding: 14px 30px;
            font-size: 15px;
          }
        }
               /* ============================================
           REMOVE TOP WHITE SPACE
        ============================================ */
        body {
          margin: 0 !important;
          padding: 0 !important;
        }
      `}</style>
    </div>
  );
};

export default Citation;